import { Dialog, DialogContent, DialogTitle, FormHelperText, Grid, TextField, Tooltip } from '@mui/material';
import _ from "lodash";
import { useState, useEffect, useRef } from 'react';
import { mapToOrganizationDetails, NewOrganization, OrganizationDetails } from '../../models/organization.model';
import { OrganizationService } from '../../services/OrganizationService';
import ButtonComponent from '../shared/button/button.component';
import { buttonLabels, ToasterTexts } from '../shared/constants';
import { LoadOrganizations } from '../../actions';
import setToast from '../../helpers/toaster.helper';
import { ToasterType } from '../shared/toaster/toaster.model';
import { validateForm } from '../shared/form-validation';
import { Autocomplete } from '@mui/material';
import { countries, states } from "../../helpers/countries";
import { ExternalServices } from '../../services/external.service';
import { UtilityService } from '../../services/UtilityService';
import industries from "../shared/Industry.json"
import { CancelIcon } from '../../icon';
import { useSelector } from "react-redux";
import * as selector from "../../selectors/selectors"
import { useDispatch } from 'react-redux';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';

interface Props {
    ViewOrganization: boolean;
    handleClose: () => void;
    SelectedOrganizationId: string,
    IsEdit: boolean,
    SelectedOrganization?: NewOrganization
}

const ViewOrganization = (props: Props) => {
    const dispatch = useDispatch()
    const IsMobileDevice: boolean = useSelector(selector.isMobileDevice)
    const [userinput, setUserinput] = useState<OrganizationDetails>({
        ID: "",
        Name: "",
        Address: "",
        MobileNumber: "",
        Email: "",
        ZipCode: "",
        Reference: "",
        Industry: "",
        HouseNumber: "",
        City: "",
        State: "",
        Country: "",
        IsNewSubFlow: true
    })
    const [showNotification, setShowNotification] = useState<boolean>(false)
    const [notificationText, setNotificationText] = useState<string>("")
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [errors, setErrors] = useState({
        Name: "",
        Email: "",
        ZipCode: "",
        Address: "",
        Reference: "",
        City: "",
        State: "",
        Country: "",
        MobileNumber: "",
        Industry: "",
        HouseNumber: "",
    })

    useEffect(() => {
        if (props.IsEdit && props.SelectedOrganizationId) {
            OrganizationService.getOrganizationById(props.SelectedOrganizationId).then(res => {
                delete res.data.errors;
                console.log(mapToOrganizationDetails(res.data))
                setUserinput({ ...mapToOrganizationDetails(res.data) })
            })
        }
    }, [])

    const inputRef = useRef(null)
    const handleChange = (name: any, value: any) => {
        const inputElement = inputRef.current;
        if (!inputElement) return;
        const cursorStart = inputElement.selectionStart || 0;
        validateForm({ [name]: value }, (Errors) => {
            setErrors({ ...errors, ...Errors });
        });
        const userdata = { ...userinput, [name]: value }
        const oldValue = inputElement.value;
        const rawDigits = userdata.MobileNumber.replace(/[^\d]/g, "");
        const formattedValue = UtilityService.maskPhoneNumber(rawDigits);
        setUserinput({ ...userdata, MobileNumber: rawDigits })
        const newCursorPos = calculateNewCursorPos(oldValue, formattedValue, cursorStart);
        requestAnimationFrame(() => {
            if (document.activeElement === inputElement) {
                inputElement.value = formattedValue;
                inputElement.setSelectionRange(newCursorPos, newCursorPos);
            }
        });
    };

    const calculateNewCursorPos = (oldValue, newValue, oldCursorPos) => {
        const digitsBeforeCursor = oldValue
            .slice(0, oldCursorPos)
            .replace(/[^\d]/g, '').length;
        let newPos = 0;
        let digitCount = 0;
        while (digitCount < digitsBeforeCursor && newPos < newValue.length) {
            if (/\d/.test(newValue[newPos])) {
                digitCount++;
            }
            newPos++;
        }
        return newPos;
    };

    const formSubmit = (event: any) => {
        event.preventDefault();
        if (validateForm({
            Name: userinput.Name, Email: userinput.Email, ZipCode: userinput.ZipCode, City: userinput.City, Address: userinput.Address, State: userinput.State, MobileNumber: userinput.MobileNumber, Industry: userinput.Industry, HouseNumber: userinput.HouseNumber, Country: userinput.Country, Reference: userinput.Reference
        }, (Errors) => {
            setErrors({ ...errors, ...Errors })
        })) {
            setIsLoading(true)
            const data = {
                id: userinput.ID,
                address: userinput.Address,
                aptNumber: userinput.HouseNumber,
                city: userinput.City,
                country: userinput.Country,
                email: userinput.Email,
                name: userinput.Name,
                reference: userinput.Reference,
                state: userinput.State,
                zip: userinput.ZipCode,
                number: UtilityService.unmaskPhoneNumber(userinput.MobileNumber),
                industry: userinput.Industry.toString(),
                IsNewSubFlow: userinput.IsNewSubFlow
            }

            if (!!userinput.ID) {
                OrganizationService.updateOrganization(data).then(res => {
                    setIsLoading(false)
                    props.handleClose();
                    LoadOrganizations(dispatch);
                    setToast(ToasterTexts.ORGANIZATION_EDIT_SUCCESS.replace('<Name>', data.name), ToasterType.SUCCESS)
                }).catch(err => {
                    setToast(err.response.data.message, ToasterType.DANGER);
                    setShowNotification(true)
                    setNotificationText(err.response.data.message)
                    setIsLoading(false)
                });
            } else {
                OrganizationService.createOrganization(data).then(res => {
                    setIsLoading(false)
                    LoadOrganizations(dispatch);
                    props.handleClose();
                    setToast(ToasterTexts.ORGANIZATION_ADD_SUCCESS.replace('<Name>', data.name), ToasterType.SUCCESS)
                }).catch(err => {
                    setToast(err.response.data.message, ToasterType.DANGER);
                    setShowNotification(true)
                    setNotificationText(err.response.data.message)
                    setIsLoading(false)
                });
            }
        }
    }

    const onZipCodeChange = () => {
        if (!userinput.ZipCode || userinput.ZipCode.length < 2) {
            return;
        }
        setUserinput({ ...userinput, City: "", State: "", Country: "" })
        ExternalServices.zipLookup(userinput.ZipCode).then(data => {
            setUserinput({ ...userinput, ...data })
            setErrors({
                ...errors,
                City: data.City ? "" : errors.City,
                State: data.State ? "" : errors.State,
                Country: data.Country ? "" : errors.Country,
            });
        })
            .catch(error => setErrors({ ...error, ZipCode: error }));
    }

    const handleIndustryType = (e: any, value: any | null) => {
        if (value) {
            setUserinput({ ...userinput, Industry: value.Value })
            setErrors({ ...errors, Industry: "" })
        }
        else {
            setUserinput({ ...userinput, Industry: "" })
            setErrors({ ...errors, Industry: "Industry cannot be blank" })
        }
    }

    const handleStateChange = (e: any, value: any | null) => {
        if (value) {
            setUserinput({ ...userinput, State: value.ISO })
            setErrors({ ...errors, State: "" })
        }
        else {
            setUserinput({ ...userinput, State: "" })
            setErrors({ ...errors, State: "State cannot be blank" })
        }
    }

    const handleCountryChange = (e: any, value: any | null) => {
        if (value) {
            setUserinput({ ...userinput, Country: value.ISO })
            setErrors({ ...errors, Country: "" })
        }
        else {
            setUserinput({ ...userinput, Country: "" })
            setErrors({ ...errors, Country: "Country cannot be blank" })
        }
    }
    return (
        <>
            {!IsMobileDevice ?
                <Dialog
                    maxWidth="sm"
                    fullWidth={true}
                    // onClose={props.handleClose}
                    aria-labelledby="customized-dialog-title"
                    BackdropProps={{ style: { backgroundColor: "transparent" } }}
                    open={props.ViewOrganization}

                    disableEscapeKeyDown
                    classes={{ paper: "br-2 dialogue-shadow" }}>
                    <DialogTitle className="dialog-header" id="customized-dialog-title">
                        <div className="h2-regular color-display-title d-flex" id="customized-dialog-title">
                            {props.IsEdit ? "Edit Organization" : "Add Organization"}
                            <Tooltip title='Close' placement='bottom'>
                                <span className={"ml-auto"}><CancelIcon className={"c-pointer ml-auto"} onClick={props.handleClose} /></span></Tooltip>
                        </div>
                    </DialogTitle>
                    <DialogContent className="ox-hidden">
                        <form noValidate autoComplete="off" className="m-auto" onSubmit={formSubmit}>
                            <Grid container direction="row" spacing={2}>
                                <Grid item xs={6} className="mt-2">
                                    <TextField autoFocus
                                        name="Name"
                                        placeholder="Name"
                                        label="Name"
                                        size="small"
                                        required
                                        fullWidth
                                        variant="outlined"
                                        value={userinput.Name}
                                        onChange={(e) =>
                                            handleChange(e.target.name, e.target.value)
                                        }
                                        InputProps={{ classes: { input: 'h4-regular color-text-field-font' } }}
                                        autoComplete='no'
                                    />
                                    <FormHelperText className="h5-regular color-primary-danger t-align-right">
                                        {errors.Name}
                                    </FormHelperText>
                                </Grid>
                                <Grid item xs={6} className="mt-2">
                                    <TextField
                                        placeholder="Reference"
                                        name="Reference"
                                        label="Reference"
                                        variant="outlined"
                                        size="small"
                                        required
                                        fullWidth
                                        disabled={props.IsEdit}
                                        value={userinput.Reference}
                                        onChange={(e) =>
                                            handleChange(e.target.name, e.target.value)
                                        }
                                        InputProps={{ classes: { input: 'h4-regular color-text-field-font' } }}
                                        className={`${props.IsEdit ? "disabled bg-color-primary-background-pink" : ""}`}
                                        autoComplete='no'
                                    />
                                    <FormHelperText className="h5-regular color-primary-danger t-align-right">
                                        {errors.Reference}
                                    </FormHelperText>
                                </Grid>
                                <Grid item xs={6} className="mt-1">
                                    <TextField
                                        fullWidth
                                        label="Email"
                                        name="Email"
                                        placeholder="Email"
                                        size="small"
                                        required
                                        variant="outlined"
                                        value={userinput.Email}
                                        onChange={(e) =>
                                            handleChange(e.target.name, e.target.value)
                                        }
                                        InputProps={{ classes: { input: 'h4-regular color-text-field-font' } }}
                                        autoComplete='no'
                                    />
                                    <FormHelperText className="h5-regular color-primary-danger t-align-right">
                                        {errors.Email}
                                    </FormHelperText>
                                </Grid>
                                <Grid item xs={6} className="mt-1">
                                    <TextField
                                        fullWidth
                                        label="Mobile Number"
                                        name="MobileNumber"
                                        placeholder="Mobile Number"
                                        size="small"
                                        required
                                        variant="outlined"
                                        value={UtilityService.maskPhoneNumber(userinput.MobileNumber, '')}
                                        onChange={(e) =>
                                            handleChange(e.target.name, e.target.value)
                                        }
                                        InputProps={{ classes: { input: 'h4-regular color-text-field-font' } }}
                                        autoComplete='no'
                                        inputRef={inputRef}
                                    />
                                    <FormHelperText className="h5-regular color-primary-danger t-align-right">
                                        {errors.MobileNumber}
                                    </FormHelperText>
                                </Grid>
                                <Grid item xs={12} className="mt-1">
                                    <Autocomplete
                                        size="small"
                                        className={"w-fill"}
                                        classes={{ input: 'h4-regular color-text-field-font', paper: 'bg-color-menu-bg box-shadow-none dialogue-shadow' }}
                                        options={industries}
                                        getOptionLabel={(option) => option.Name}
                                        onChange={handleIndustryType}
                                        value={industries.find((x: any) => x.Value === userinput.Industry) || { Name: "", Value: "" }}
                                        renderOption={(defaultprops, option: any) => (
                                            <li {...defaultprops}>
                                                <span className={"c-pointer h4-regular color-menu-list-font-color p-2 d-flex justify-between w-p-90"}> {option?.Name}</span>
                                            </li>
                                        )}
                                        renderInput={(params) => <TextField {...params} name="industry" size="small" required variant="outlined" label="Industry" placeholder="Industry" />}
                                    />
                                    <FormHelperText className="h5-regular color-primary-danger t-align-right">
                                        {errors.Industry}
                                    </FormHelperText>
                                </Grid>
                                <Grid item xs={6} className="mt-1">
                                    <TextField
                                        fullWidth
                                        label="House number"
                                        name="HouseNumber"
                                        placeholder="House number"
                                        size="small"
                                        required
                                        variant="outlined"
                                        value={userinput.HouseNumber}
                                        onChange={(e) =>
                                            handleChange(e.target.name, e.target.value)
                                        }
                                        InputProps={{ classes: { input: 'h4-regular color-text-field-font' } }}
                                        autoComplete='no'
                                    />
                                    <FormHelperText className="h5-regular color-primary-danger t-align-right">
                                        {errors.HouseNumber}
                                    </FormHelperText>
                                </Grid>
                                <Grid item xs={6} className="mt-1">
                                    <TextField
                                        fullWidth
                                        label="Street Name"
                                        name="Address"
                                        placeholder="Street Name"
                                        size="small"
                                        required
                                        variant="outlined"
                                        value={userinput.Address}
                                        onChange={(e) =>
                                            handleChange(e.target.name, e.target.value)
                                        }
                                        InputProps={{ classes: { input: 'h4-regular color-text-field-font' } }}
                                        autoComplete='no'
                                    />
                                    <FormHelperText className=" h5-regular color-primary-danger t-align-right">
                                        {errors.Address}
                                    </FormHelperText>
                                </Grid>

                                <Grid item xs={6} className="mt-1">
                                    <TextField
                                        fullWidth
                                        label="ZipCode"
                                        name="ZipCode"
                                        placeholder="ZipCode"
                                        size="small"
                                        required
                                        variant="outlined"
                                        value={userinput.ZipCode}
                                        onChange={(e) =>
                                            handleChange(e.target.name, e.target.value)
                                        }
                                        onKeyUp={onZipCodeChange}
                                        InputProps={{ classes: { input: 'h4-regular color-text-field-font' } }}
                                        autoComplete='no'
                                    />
                                    <FormHelperText className=" h5-regular color-primary-danger t-align-right">
                                        {errors.ZipCode}
                                    </FormHelperText>
                                </Grid>

                                <Grid item xs={6} className="mt-1">
                                    <TextField
                                        fullWidth
                                        label="City"
                                        name="City"
                                        placeholder="City"
                                        size="small"
                                        required
                                        variant="outlined"
                                        value={userinput.City}
                                        onChange={(e) =>
                                            handleChange(e.target.name, e.target.value)
                                        }
                                        InputProps={{ classes: { input: 'h4-regular color-text-field-font' } }}
                                        autoComplete='no'
                                    />
                                    <FormHelperText className=" h5-regular color-primary-danger t-align-right">
                                        {errors.City}
                                    </FormHelperText>
                                </Grid>
                                <Grid item xs={6} className="mt-1">
                                    <Autocomplete
                                        size="small"
                                        className={"w-fill"}
                                        classes={{ input: 'h4-regular color-text-field-font', paper: 'bg-color-menu-bg box-shadow-none dialogue-shadow' }}
                                        options={(states as any)[userinput.Country] || []}
                                        getOptionLabel={(option) => option.Name}
                                        onChange={handleStateChange}
                                        value={(states as any)[userinput.Country]?.find((x: any) => x.ISO === userinput.State) || { ISO: "", Name: "" }}
                                        disabled={!userinput.Country}
                                        renderOption={(defaultprops, option: any) => (
                                            <li{...defaultprops} >
                                                <span className={"c-pointer h4-regular color-menu-list-font-color p-2 d-flex justify-between w-p-80"}> {option.Name}</span>
                                            </li>
                                        )}
                                        renderInput={(params) => <TextField {...params} name="state" size="small" required variant="outlined" label="State" placeholder="State" />}
                                    />
                                    <FormHelperText className=" h5-regular color-primary-danger t-align-right">
                                        {errors.State}
                                    </FormHelperText>
                                </Grid>
                                <Grid item xs={6} className="mt-1">
                                    <Autocomplete
                                        size="small"
                                        className={"w-fill"}
                                        classes={{ input: 'h4-regular color-text-field-font', paper: 'bg-color-menu-bg box-shadow-none dialogue-shadow' }}
                                        options={countries}
                                        getOptionLabel={(option) => option.Name}
                                        onChange={handleCountryChange}
                                        value={countries.find((x: any) => x.ISO === userinput.Country) || { ISO: "", Name: "" }}
                                        renderOption={(defaultprops, option: any) => (
                                            <li {...defaultprops}>
                                                <span className={"c-pointer h4-regular color-menu-list-font-color p-2 d-flex justify-between w-p-80"}> {option.Name}</span>
                                            </li>
                                        )}
                                        renderInput={(params) => <TextField {...params} name="country" size="small" required variant="outlined" label="Country" placeholder="Country" />}
                                    />
                                    <FormHelperText className=" h5-regular color-primary-danger t-align-right">
                                        {errors.Country}
                                    </FormHelperText>
                                </Grid>
                                {props.IsEdit && <Grid item xs={12} className="mt-1">
                                    <ToggleButtonGroup
                                        sx={{
                                            '& .Mui-selected': {
                                                backgroundColor: '#1450ff !important',
                                                color: 'white !important',
                                                '&:hover': {
                                                    backgroundColor: '#2563eb !important'
                                                }
                                            }
                                        }}
                                        disabled={(props?.SelectedOrganization?.SeatUsage?.length > 0)}
                                        value={userinput.IsNewSubFlow ? 'yes' : 'no'}
                                        exclusive
                                        onChange={(_, value) => {
                                            setUserinput({ ...userinput, IsNewSubFlow: value === 'yes' });
                                        }}
                                        size="small"
                                        fullWidth
                                    >
                                        <ToggleButton value="yes">New Organization</ToggleButton>
                                        <ToggleButton value="no">Existing Organization</ToggleButton>
                                    </ToggleButtonGroup>
                                </Grid>}
                            </Grid>
                            <div className="d-flex float-right mt-3 mb-2">
                                <ButtonComponent OnClick={props.handleClose} ButtonLabel={buttonLabels.CANCEL} ClassName={"subtext mr-1 c-pointer"} Variant={"secondary-btn-small"} />
                                <ButtonComponent Type={"submit"} ButtonLabel={buttonLabels.SAVE} Loading={isLoading} ClassName={"subtext c-pointer px-4 mr-1"} Variant={"primary-btn-small"} />
                            </div>
                        </form>
                    </DialogContent>

                </Dialog > :
                <div className="d-flex flex-direction h-v-90 scroll">
                    <form noValidate autoComplete="no" className="m-2 mt-3">
                        <Grid container direction="row" spacing={2}>
                            <Grid item xs={12} className="mt-1">
                                <TextField autoFocus
                                    name="Name"
                                    placeholder="Name"
                                    label="Name"
                                    size="small"
                                    required
                                    fullWidth
                                    variant="outlined"
                                    value={userinput.Name}
                                    onChange={(e) =>
                                        handleChange(e.target.name, e.target.value)
                                    }
                                    InputProps={{ classes: { input: 'h2 color-text-field-font' } }}
                                    autoComplete='no'
                                />
                                <FormHelperText className="h5 color-primary-danger t-align-right">
                                    {errors.Name}
                                </FormHelperText>
                            </Grid>
                            <Grid item xs={12} className="mt-1">
                                <TextField
                                    placeholder="Reference"
                                    name="Reference"
                                    label="Reference"
                                    variant="outlined"
                                    required
                                    size="small"
                                    fullWidth
                                    disabled={props.IsEdit}
                                    value={userinput.Reference}
                                    onChange={(e) =>
                                        handleChange(e.target.name, e.target.value)
                                    }
                                    InputProps={{ classes: { input: 'h2 color-text-field-font' } }}
                                    className={`${props.IsEdit ? "disabled bg-color-primary-background-pink" : ""}`}
                                    autoComplete='no'
                                />
                                <FormHelperText className="h5 color-primary-danger t-align-right">
                                    {errors.Reference}
                                </FormHelperText>
                            </Grid>

                            <Grid item xs={12} className="mt-1">
                                <TextField
                                    fullWidth
                                    label="Email"
                                    name="Email"
                                    placeholder="Email"
                                    size="small"
                                    required
                                    variant="outlined"
                                    value={userinput.Email}
                                    onChange={(e) =>
                                        handleChange(e.target.name, e.target.value)
                                    }
                                    InputProps={{ classes: { input: 'h2 color-text-field-font' } }}
                                    autoComplete='no'
                                />
                                <FormHelperText className="h5 color-primary-danger t-align-right">
                                    {errors.Email}
                                </FormHelperText>
                            </Grid>
                            <Grid item xs={12} className="mt-1">
                                <TextField
                                    fullWidth
                                    label="Mobile Number"
                                    name="MobileNumber"
                                    placeholder="Mobile Number"
                                    size="small"
                                    required
                                    variant="outlined"
                                    value={UtilityService.maskPhoneNumber(userinput.MobileNumber, '')}
                                    onChange={(e) =>
                                        handleChange(e.target.name, e.target.value)
                                    }
                                    InputProps={{ classes: { input: 'h2 color-text-field-font' } }}
                                    autoComplete='no'
                                    inputRef={inputRef}
                                />
                                <FormHelperText className="h5 color-primary-danger t-align-right">
                                    {errors.MobileNumber}
                                </FormHelperText>
                            </Grid>
                            <Grid item xs={12} className="mt-1">
                                <Autocomplete
                                    size="small"
                                    className={"w-fill"}
                                    classes={{ input: 'h2 color-text-field-font', paper: 'bg-color-menu-bg box-shadow-none dialogue-shadow' }}
                                    options={industries}
                                    getOptionLabel={(option) => option?.Name}
                                    onChange={handleIndustryType}
                                    value={industries.find(x => x.Value === userinput.Industry)}
                                    renderOption={(defaultprops, option: any) => (
                                        <li {...defaultprops} className={"c-pointer h2 color-primary-dark p-2 d-flex justify-between w-p-90"}>
                                            <div> {option?.Name}</div></li>
                                    )}
                                    renderInput={(params) => <TextField {...params} name="industry" size="small" required variant="outlined" label="Industry" placeholder="Industry" />}
                                />
                                <FormHelperText className="h5 color-primary-danger t-align-right">
                                    {errors.Industry}
                                </FormHelperText>
                            </Grid>
                            <Grid item xs={12} className="mt-1">
                                <TextField
                                    fullWidth
                                    label="House number"
                                    name="HouseNumber"
                                    placeholder="House number"
                                    size="small"
                                    required
                                    variant="outlined"
                                    value={userinput.HouseNumber}
                                    onChange={(e) =>
                                        handleChange(e.target.name, e.target.value)
                                    }
                                    InputProps={{ classes: { input: 'h2 color-text-field-font' } }}
                                    autoComplete='no'
                                />
                                <FormHelperText className="h5 color-primary-danger t-align-right">
                                    {errors.HouseNumber}
                                </FormHelperText>
                            </Grid>
                            <Grid item xs={12} className="mt-1">
                                <TextField
                                    fullWidth
                                    label="Street Name"
                                    name="Address"
                                    placeholder="Street Name"
                                    size="small"
                                    required
                                    variant="outlined"
                                    value={userinput.Address}
                                    onChange={(e) =>
                                        handleChange(e.target.name, e.target.value)
                                    }
                                    InputProps={{ classes: { input: 'h2 color-text-field-font' } }}
                                    autoComplete='no'
                                />
                                <FormHelperText className="h5 color-primary-danger t-align-right">
                                    {errors.Address}
                                </FormHelperText>
                            </Grid>
                            <Grid item xs={12} className="mt-1">
                                <TextField
                                    fullWidth
                                    label="ZipCode"
                                    name="ZipCode"
                                    placeholder="ZipCode"
                                    size="small"
                                    required
                                    variant="outlined"
                                    value={userinput.ZipCode}
                                    onChange={(e) =>
                                        handleChange(e.target.name, e.target.value)
                                    }
                                    onKeyUp={onZipCodeChange}
                                    InputProps={{ classes: { input: 'h2 color-text-field-font' } }}
                                    autoComplete='no'
                                />
                                <FormHelperText className="h5 color-primary-danger t-align-right">
                                    {errors.ZipCode}
                                </FormHelperText>
                            </Grid>

                            <Grid item xs={12} className="mt-1">
                                <TextField
                                    fullWidth
                                    label="City"
                                    name="City"
                                    placeholder="City"
                                    size="small"
                                    required
                                    variant="outlined"
                                    value={userinput.City}
                                    onChange={(e) =>
                                        handleChange(e.target.name, e.target.value)
                                    }
                                    InputProps={{ classes: { input: 'h2 color-text-field-font' } }}
                                    autoComplete='no'
                                />
                                <FormHelperText className="h5 color-primary-danger t-align-right">
                                    {errors.City}
                                </FormHelperText>
                            </Grid>

                            <Grid item xs={12} className="mt-1">
                                <Autocomplete
                                    size="small"
                                    className={"w-fill"}
                                    classes={{ input: 'h2 color-text-field-font', paper: 'bg-color-menu-bg box-shadow-none dialogue-shadow' }}
                                    options={(states as any)[userinput.Country] || []}
                                    getOptionLabel={(option) => option.Name}
                                    onChange={handleStateChange}
                                    value={(states as any)[userinput.Country]?.find((x: any) => x.ISO === userinput.State) || { ISO: "", Name: "" }}
                                    disabled={!userinput.Country}
                                    renderOption={(defaultprops, option: any) => (
                                        <li {...defaultprops} className={"c-pointer h2 color-primary-dark p-2 d-flex justify-between w-p-90"}>
                                            <div> {option.Name}</div></li>
                                    )}
                                    renderInput={(params) => <TextField {...params} name="state" size="small" required variant="outlined" label="State" placeholder="State" />}
                                />
                                <FormHelperText className="h5 color-primary-danger t-align-right">
                                    {errors.State}
                                </FormHelperText>
                            </Grid>
                            <Grid item xs={12} className="mt-1">
                                <Autocomplete
                                    size="small"
                                    className={"w-fill"}
                                    classes={{ input: 'h2 color-text-field-font', paper: 'bg-color-menu-bg box-shadow-none dialogue-shadow' }}
                                    options={countries}
                                    getOptionLabel={(option) => option.Name}
                                    onChange={handleCountryChange}
                                    value={countries.find((x: any) => x.ISO === userinput.Country) || { ISO: "", Name: "" }}
                                    renderOption={(defaultprops, option: any) => (
                                        <li {...defaultprops} className={" c-pointer h2 color-primary-dark p-2 d-flex justify-between w-p-90"}>
                                            <div> {option.Name}</div></li>
                                    )}
                                    renderInput={(params) => <TextField {...params} name="country" size="small" required variant="outlined" label="Country" placeholder="Country" />}
                                />
                                <FormHelperText className="h5 color-primary-danger t-align-right">
                                    {errors.Country}
                                </FormHelperText>
                            </Grid>
                            {props.IsEdit && <Grid item xs={12} className="mt-1">
                                <ToggleButtonGroup
                                    sx={{
                                        '& .Mui-selected': {
                                            backgroundColor: '#1450ff !important',
                                            color: 'white !important',
                                            '&:hover': {
                                                backgroundColor: '#2563eb !important'
                                            }
                                        }
                                    }}
                                    disabled={(props?.SelectedOrganization?.SeatUsage?.length > 0)}
                                    value={userinput.IsNewSubFlow ? 'yes' : 'no'}
                                    exclusive
                                    onChange={(_, value) => {
                                        setUserinput({ ...userinput, IsNewSubFlow: value === 'yes' });
                                    }}
                                    size="small"
                                    fullWidth
                                >
                                    <ToggleButton value="yes">New Organization</ToggleButton>
                                    <ToggleButton value="no">Existing Organization</ToggleButton>
                                </ToggleButtonGroup>
                            </Grid>}
                        </Grid>
                    </form>
                    <div className={`mx-2 my-3`}>
                        <ButtonComponent Type={"submit"} ButtonLabel={buttonLabels.SAVE.toUpperCase()} Loading={isLoading} ClassName={"br-6 w-p-100 justify-center c-pointer px-4 mr-1"} Variant={"primary-btn-small"} OnClick={formSubmit} />

                        <ButtonComponent OnClick={props.handleClose} ButtonLabel={buttonLabels.CANCEL.toUpperCase()} ClassName={"br-6 w-p-100 justify-center c-pointer mt-2"} Variant={"secondary-btn-small"} />
                    </div>
                </div>
            }
        </>
    )
}

export default ViewOrganization